import { Button } from "../ui/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faIndianRupeeSign,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import homeService from "../../features/home/services/home.service";
import type { ActiveVerticalDetail } from "../../features/home/models";
import { useState, useEffect, useRef } from "react";
import { GetBalanceResponse } from "../../features/home/models/response/getBalanceResponse";
import { Link, useNavigate } from "react-router-dom";
import { localStorageService } from "../../services";
import { useSelector } from "react-redux";
import {
  selectInboxUnreadCount,
  selectUserAvatar,
  selectUserProfile,
} from "../../features/users/selectors/userProfileSelectors";
import { getAvatarById } from "../../features/users/components/avatar/avatar.config";
import localization from "../../config/localization";
import { ConfirmationModal } from "../ui/modals/ConfirmationModal";
import { useLanguage } from "../../config/localization/LanguageProvider";
import { useAppDispatch } from "../../store/hooks";
import { ConfirmationModal as SessionExpiredModal } from "../ui/modals/ConfirmationModal";
import { SupportedLanguages } from "../../models/constants/supportedLanguages";
import Sidebar from "./sidebar/Sidebar";
import { UserWalletWidget } from "../../features/users/components/UserWalletWidget";
import { changeLoaderState } from "../../reducers/appStateReducer";
import { RegistrationType } from "../../models/constants/registrationType";
import { authService } from "../../features/auth";
import {
  RESET_PASSWORD_TYPE_ONE_CLICK,
  RESET_PASSWORD_TYPE_WHATSAPP,
  RESET_PASSWORD_TYPES,
} from "../../features/auth/models/resetPasswordType";
import { DeviceType } from "../../models/constants/deviceType";
import userService from "../../features/users/services/user.service";
import { AnnouncementRetrieveItem } from "../../features/users/models/response/announcementRetrieveResponse";
import { toastService } from "../ui/toast/toast.service";
import { userPendingStore } from "../../features/users/store/userPendingData.store";
import { formatMainWalletAmountDisplay } from "../../utils/walletAmountDisplay";
import { ExchangeWindowClose } from "../../assets/js/common";
import { BLOB_IMAGE_URL } from "../../config/appConfig";
import { BlobContainerConsts } from "../../models/constants/blobContainerConsts";
import { GameVertical } from "../../models/constants/gameVertical";

const SPORTS_VERTICAL = GameVertical.Sports.toLowerCase();
const SAP_VERTICAL = GameVertical.SAP.toLowerCase();

const filterActiveVerticalsForNav = (
  details: ActiveVerticalDetail[],
  userVerticalsCsv: string | null | undefined,
) => {
  const userVerticalsList = (userVerticalsCsv ?? "")
    .toLowerCase()
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return details.filter((vertical) => {
    const nameLower = vertical.name.toLowerCase();
    const isSportsIndia =
      nameLower === GameVertical.SportsIndia.toLowerCase() ||
      nameLower === "sports india";

    if (nameLower === SPORTS_VERTICAL) {
      return !userVerticalsList.includes(SPORTS_VERTICAL);
    }
    if (isSportsIndia) {
      return !userVerticalsList.includes(SAP_VERTICAL);
    }
    return !userVerticalsList.some((x) => x === nameLower);
  });
};

interface HeaderProps {
  deviceType: string;
  isAuthenticated?: boolean;
  onLoginClick?: () => void;
  onJoinNowClick?: () => void;
}

export function Header({
  deviceType,
  isAuthenticated = false,
  onLoginClick,
  onJoinNowClick,
}: HeaderProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const profile = useSelector(selectUserProfile);
  const avatar = useSelector(selectUserAvatar);

  const [userBalanceResponse, setUserBalance] = useState<GetBalanceResponse>();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isWalletWidgetOpen, setIsWalletWidgetOpen] = useState(false);
  const [showSportsIndia, setShowSportsIndia] = useState(true);
  const [userVerticalsCsv, setUserVerticalsCsv] = useState<string[]>([]);

  const profileMenuRef = useRef<HTMLDivElement>(null);
  const walletWidgetRef = useRef<HTMLDivElement>(null);
  const { setLanguage } = useLanguage();

  const [verticals, setVerticals] = useState<ActiveVerticalDetail[]>([]);

  const redirectionLock = useRef(false);
  const oneClickTimerRef = useRef<number | null>(null);

  const ONE_CLICK_REDIRECT_KEY = "oneClickResetRedirectAt";

  const inboxUnreadCount = useSelector(selectInboxUnreadCount);

  const clearOneClickTimer = () => {
    if (oneClickTimerRef.current !== null) {
      window.clearTimeout(oneClickTimerRef.current);
      oneClickTimerRef.current = null;
    }
  };

  const fetchBalance = async () => {
    const response = await homeService.fetchUserBalance();
    if (response.isSuccess) {
      setUserBalance(response?.data || undefined);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchBalance();
      const interval = setInterval(fetchBalance, 5000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (profile) {
      setLanguage(profile.languageCode || SupportedLanguages.English);
      if (
        profile.registrationType &&
        profile.registrationType.toLowerCase() ===
        RegistrationType.ManualOrWhatsApp.toLowerCase() &&
        !profile.isOneTimePasswordChanged
      ) {
        redirectionLock.current = true;
        navigate("/reset-password", {
          state: {
            showBackButton: false,
            resetPasswordType: RESET_PASSWORD_TYPES.find(
              (t) => t.type === RESET_PASSWORD_TYPE_WHATSAPP,
            ),
          },
        });
      } else if (profile?.registrationType && !profile?.isOneTimePasswordChanged
        && profile.registrationType === RegistrationType.OneClick && !profile.isSecondLoginDone) {
        const existingRedirectAt = localStorageService.getItem<number>(
          ONE_CLICK_REDIRECT_KEY,
        );
        const redirectAt = existingRedirectAt ?? Date.now() + 60000;

        if (!existingRedirectAt) {
          localStorageService.setItem(ONE_CLICK_REDIRECT_KEY, redirectAt);
        }

        const remainingMs = redirectAt - Date.now();
        const redirectToResetPassword = () => {
          localStorageService.removeItem(ONE_CLICK_REDIRECT_KEY);
          navigate("/reset-password", {
            state: {
              showBackButton: true,
              resetPasswordType: RESET_PASSWORD_TYPES.find(
                (t) => t.type === RESET_PASSWORD_TYPE_ONE_CLICK,
              ),
              isAllowedForReset: true,
            },
          });
        };

        clearOneClickTimer();

        if (remainingMs <= 0) {
          redirectToResetPassword();
        } else {
          oneClickTimerRef.current = window.setTimeout(
            redirectToResetPassword,
            remainingMs,
          );
        }
      } else {
        clearOneClickTimer();
        localStorageService.removeItem(ONE_CLICK_REDIRECT_KEY);
      }
    }

    return () => {
      clearOneClickTimer();
    };
  }, [profile, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      userPendingStore.getState().fetchPendingData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        dispatch(changeLoaderState(true));
        if (!isAuthenticated) {
          return;
        }

        const userId = localStorageService.getItem<string>("userId");
        if (!userId) {
          return;
        }

        const announcementLanguageCode = language;
        const response = await userService.retrieveAnnouncements({
          LanguageCode: announcementLanguageCode,
        });
        if (!response.isSuccess || !response.data?.details) {
          return;
        }
        handleAnnouncementToasts(userId, response.data.details);
      } finally {
        dispatch(changeLoaderState(false));
      }
    };

    fetchAnnouncements();
  }, [isAuthenticated, profile?.languageCode]);

  useEffect(() => {
    const loadVerticals = async () => {
      try {
        dispatch(changeLoaderState(true));
        const response = await homeService.fetchActiveVerticals();
        if (!response.isSuccess || !response.data?.details) {
          setVerticals([]);
          return;
        }

        let details = response.data.details.map((vertical) => ({
          ...vertical,
          imageUrl: fetchVerticalIcon(vertical.name),
        }));
        setShowSportsIndia(true);
        if (isAuthenticated) {
          const userVerticals = await homeService.fetchUserVerticals();
          if (userVerticals.isSuccess && userVerticals.data) {
            const userVerticalsList = (userVerticals.data.verticalsCsv ?? "")
              .toLowerCase()
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            setUserVerticalsCsv(userVerticalsList);
            setShowSportsIndia(!userVerticalsList.includes(SAP_VERTICAL));
            details = filterActiveVerticalsForNav(
              details,
              userVerticals.data.verticalsCsv,
            );
          }
        }
        setVerticals(details);
      } finally {
        dispatch(changeLoaderState(false));
      }
    };
    loadVerticals();
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!userVerticalsCsv?.length) return;

    const pathname = window.location.pathname.toLowerCase();

    const pathToVerticalMap: Record<string, string> = {
      bharatsportsbook: "sap",
      sportsbookplus: "sports",
      casino: "casino",
      gamelaunch: "casino",
      exchange: "exchange/Wickets9",
    };

    const shouldRedirect = Object.entries(pathToVerticalMap).some(
      ([pathKey, verticalCode]) =>
        pathname.includes(pathKey.toLowerCase()) &&
        userVerticalsCsv.includes(verticalCode.toLowerCase()),
    );

    if (shouldRedirect) {
      navigate("/");
    }
  }, [userVerticalsCsv, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }

      if (
        walletWidgetRef.current &&
        !walletWidgetRef.current.contains(event.target as Node)
      ) {
        setIsWalletWidgetOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const decodeHtml = (value: string) => {
    const parser = new DOMParser();
    return (
      parser.parseFromString(value, "text/html").documentElement.textContent ??
      value
    );
  };

  const getAnnouncementIdentifier = (
    announcement: AnnouncementRetrieveItem,
  ) => {
    const referenceTime = announcement.modifiedTime || announcement.createdTime;
    return `${announcement.id}.${referenceTime}`;
  };

  const handleAnnouncementToasts = (
    userId: string,
    announcements: AnnouncementRetrieveItem[],
  ) => {
    const announcementMap =
      localStorageService.getItem<Record<string, string[]>>(
        "announcementKey",
      ) ?? {};
    const existingKeys = announcementMap[userId] ?? [];
    const nextKeys = [...existingKeys];

    announcements.length > 0 &&
      announcements.forEach((announcement) => {
        const announcementIdentifier = getAnnouncementIdentifier(announcement);

        if (
          existingKeys.length === 0 ||
          !existingKeys.includes(announcementIdentifier)
        ) {
          toastService.warning(
            decodeHtml(announcement.announcementDetails),
            undefined,
            true,
          );
        }

        if (!nextKeys.includes(announcementIdentifier)) {
          nextKeys.push(announcementIdentifier);
        }
      });

    announcementMap[userId] = nextKeys;
    localStorageService.setItem("announcementKey", announcementMap);
  };

  const fetchVerticalIcon = (name: string): string => {
    switch (name) {
      case GameVertical.Exchange:
        return "/icons/ic-exchange.svg";
      case GameVertical.Casino:
        return "/icons/ic-casino.svg";
      case GameVertical.Sports:
        return "/icons/ic-sports.svg";
      default:
        return "/icons/ic-exchange.svg";
    }
  };
  const fetchPath = (name: string) => {
    if (name.toLocaleLowerCase() === "sports") {
      return "/sportsbookplus";
    } else if (name.toLocaleLowerCase() === "sports-india") {
      return "/bharatsportsbook";
    } else if (name.toLocaleLowerCase() === "exchange") {
      return isAuthenticated ? "exchange/Wickets9" : name.toLocaleLowerCase();
    }
    return `/${name.toLowerCase()}`;
  };

  const handleNavigate = (path: string) => {
    if (redirectionLock.current) {
      return;
    }
    navigate(path);
  };

  const handleLogout = async (callApi: boolean = true) => {
    if (callApi) {
      await authService
        .logout()
        .then((response) => {
          if (response.isSuccess) {
            localStorageService.clear();
            ExchangeWindowClose();
            handleNavigate("/");
            window.location.reload();
            dispatch(changeLoaderState(true));
            setTimeout(() => {
              dispatch(changeLoaderState(false));
            }, 1000);
          }
        })
        .finally(() => {
          userPendingStore.getState().clearPendingData();
          dispatch(changeLoaderState(false));
        });
    } else {
      document.getElementById("session-expired")?.classList.add("hidden");
      dispatch(changeLoaderState(true));
      localStorageService.clear();
      handleNavigate("/");
      window.location.reload();
      setTimeout(() => {
        userPendingStore.getState().clearPendingData();
        dispatch(changeLoaderState(false));
      }, 1000);
    }
  };

  const getAvatarSrc = () => {
    if (!avatar) {
      return "/images/avatars/avatar5.svg";
    }
    else if (getAvatarById(avatar)) {
      return getAvatarById(avatar) ?? "/images/avatars/avatar5.svg";
    }
    else {
      return `${BLOB_IMAGE_URL}${BlobContainerConsts.UserAvatar}/${avatar}`;
    }
  };

  return (
    <header
      className={`relative w-full h-20 px-4 sm:px-6 flex items-center backdrop-blur-md ${deviceType !== DeviceType.Web ? "bg-[#10101099]" : ""
        }`}
    >
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute rounded-full"
          style={{
            top: "-30px",
            right: "-20px",
            width: "170px",
            height: "91px",
            backgroundColor: "#C7184A",
            filter: "blur(64px)",
            opacity: 0.85,
          }}
        />
      </div>

      <div className="flex items-center w-full mx-auto gap-3 min-w-0 lg:max-w-[90%]">
        {
          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden xl:block">
              <Button
                onClick={() => {
                  if (redirectionLock.current) {
                    return;
                  }
                  setIsMenuOpen(true);
                }}
                className="text-white hover:text-gray-300 bg-[#202020] px-2 py-2 rounded-lg border border-[#272727] shadow-none"
              >
                <img
                  src={"/icons/ic-desktop-menu.svg"}
                  alt="Menu"
                  className="w-6 h-6 object-contain"
                />
              </Button>
            </div>

            <Link to="/" className="relative flex ">
              <img
                src="/images/logo.svg"
                alt="Logo"
                className="h-20 w-auto object-contain"
              />
            </Link>
          </div>
        }

        {/*Verticals*/}

        {deviceType === DeviceType.Web && (
          <div className="hidden lg:flex items-center gap-1 xl:gap-2 2xl:gap-6 ml-2 xl:ml-4 2xl:ml-8 text-[11px] xl:text-xs 2xl:text-sm whitespace-nowrap flex-1 min-w-0">
            {verticals.map((vertical) => (
              <Button
                key={vertical.id}
                onClick={() => handleNavigate(fetchPath(vertical.name))}
                className="flex items-center justify-center gap-1 xl:gap-2 py-3 px-1 shrink-0 shadow-none"
              >
                <img
                  className="w-6 h-6 object-contain"
                  src={vertical.imageUrl}
                  alt={vertical.name}
                  loading="lazy"
                />
                <span className="font-semibold tracking-wide whitespace-nowrap ">
                  {localization.getString(vertical.name.toLowerCase())}
                </span>
              </Button>
            ))}
            {showSportsIndia && (
              <Button
                key={"sports-india"}
                onClick={() => handleNavigate(fetchPath("sports-india"))}
                className="flex items-center justify-center gap-1 xl:gap-2 py-3 px-1 shrink-0 shadow-none"
              >
                <img
                  className="w-6 h-6 object-contain"
                  src={"/icons/ic-sports-india.svg"}
                  alt="promotion"
                />
                <span className="font-semibold tracking-wide whitespace-nowrap ">
                  {localization.sportsIndia}
                </span>
              </Button>
            )}
            <Button
              key={"promotion"}
              onClick={() => handleNavigate(fetchPath("promotion"))}
              className="flex items-center justify-center gap-1 xl:gap-2 py-3 px-1 shrink-0 shadow-none"
            >
              <img
                className="w-6 h-6 object-contain"
                src={"/icons/ic-promotion.svg"}
                alt="promotion"
              />
              <span className="font-semibold tracking-wide whitespace-nowrap ">
                {localization.promotion}
              </span>
            </Button>
          </div>
        )}

        <div className="flex items-center gap-1 xl:gap-2 2xl:gap-3 shrink-0 ml-auto">
          {!isAuthenticated && (
            <>
              <Button
                className="text-white font-medium hover:opacity-80 shadow-none capitalize"
                onClick={onLoginClick}
              >
                {localization.login.toLowerCase()}
              </Button>

              <Button
                className="bg-primary-gradient px-4 py-2 rounded-md font-semibold shadow-none"
                onClick={onJoinNowClick}
              >
                {localization.joinNow}
              </Button>
            </>
          )}

          {isAuthenticated && (
            <>
              <div className="relative" ref={walletWidgetRef}>
                <div className="flex items-center gap-2 bg-[#202020] border border-[#272727] p-1.5 rounded-md">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-black border border-purple-600">
                    <FontAwesomeIcon icon={faIndianRupeeSign} />
                  </div>

                  <span
                    className="text-[11px] xl:text-xs 2xl:text-sm font-medium text-white hover:cursor-pointer hover:text-gray-300"
                    onClick={() => setIsWalletWidgetOpen((prev) => !prev)}
                  >
                    {formatMainWalletAmountDisplay(userBalanceResponse?.amount)}
                  </span>

                  <Button
                    className="opacity-70 hover:opacity-100 shadow-none"
                    onClick={() => setIsWalletWidgetOpen((prev) => !prev)}
                  >
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`transition-all duration-300 ${isWalletWidgetOpen ? "rotate-180" : ""
                        }`}
                    />
                  </Button>

                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary-gradient">
                    <Button
                      onClick={() => { setIsWalletWidgetOpen(false); handleNavigate("/deposit") }}
                      className="hover:rotate-180 transition-all duration-300 shadow-none"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </Button>
                  </div>
                </div>

                {isWalletWidgetOpen && (
                  <UserWalletWidget
                    isOpen={isWalletWidgetOpen}
                    userBalanceResponse={userBalanceResponse!}
                  />
                )}
              </div>

              {deviceType === DeviceType.Web && (
                <div className="flex items-center gap-1 xl:gap-2 bg-[#202020] border border-[#272727] px-2 xl:px-3 py-1.5 rounded-full">
                  <div className="relative">
                    <img
                      src={"/icons/ic-desktop-inbox.svg"}
                      alt="inbox"
                      className="w-7 h-7 rounded-full cursor-pointer"
                      onClick={() => handleNavigate("/inbox")}
                    />

                    {inboxUnreadCount > 0 && (
                      <span
                        className="absolute -top-3 -right-4 
                                bg-red-500 text-white text-xs font-semibold 
                                w-7 h-7
                                rounded-full flex items-center justify-center
                                border-2 border-[#202020]"
                      >
                        {inboxUnreadCount > 9 ? "9+" : inboxUnreadCount}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="relative" ref={profileMenuRef}>
                {deviceType === DeviceType.Web ? (
                  <div className="flex items-center gap-1 xl:gap-2 bg-[#202020] border border-[#272727] px-2 xl:px-3 py-1.5 rounded-md">
                    <img
                      src={getAvatarSrc()}
                      alt="avatar"
                      className="w-7 h-7 rounded-full cursor-pointer"
                      onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                    />
                    <span
                      className="hidden 2xl:inline text-sm hover:cursor-pointer hover:text-gray-300"
                      onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                    >
                      {profile?.name ?? profile?.username}
                    </span>

                    <Button
                      className="opacity-70 hover:opacity-100 shodow-none"
                      onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                    >
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`transition-all duration-300 ease-in-out ${isProfileMenuOpen ? "rotate-180" : "rotate-0"
                          }`}
                      />
                    </Button>
                  </div>
                ) : (
                  <img
                    src={getAvatarSrc()}
                    alt="avatar"
                    className="w-10 h-10 rounded-full cursor-pointer"
                    onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                  />
                )}
                {isProfileMenuOpen && (
                  <div className="absolute top-12 right-0 w-44 bg-white rounded-lg shadow-lg overflow-hidden z-50">
                    <Button
                      onClick={() => {
                        handleNavigate("/profile");
                        setIsProfileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 shadow-none"
                    >
                      <img
                        src="/icons/ic-user.svg"
                        className="w-5 h-5"
                        alt="profile"
                      />
                      <span className="font-medium">
                        {localization.profile}
                      </span>
                    </Button>

                    <div className="h-px bg-gray-200" />

                    <Button
                      onClick={() => {
                        setIsModalOpen(true);
                        setIsProfileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 shadow-none"
                    >
                      <img
                        src="/icons/ic-logout.svg"
                        className="w-5 h-5"
                        alt="logout"
                      />
                      <span className="font-medium">{localization.logout}</span>
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {isModalOpen && (
        <ConfirmationModal
          isOpen={isModalOpen}
          title={localization.logout_confirmation}
          icon="/icons/ic-info.svg"
          blurColor="bg-[#FFE6CC]"
          confirmText="Yes"
          cancelText="No"
          onConfirm={() => handleLogout(true)}
          onCancel={() => setIsModalOpen(false)}
        />
      )}

      <SessionExpiredModal
        isOpen={true}
        id="session-expired"
        className="hidden"
        title={localization.session_error_model}
        icon="/icons/ic-error.svg"
        blurColor="bg-[#FFD4D4]"
        onConfirm={() => handleLogout(false)}
        confirmText={localization.login_again}
      />

      <Sidebar isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </header>
  );
}
